// ─────────────────────────────────────────────────────────────────────────────
// CheckFactorBar
//
// A vertical, five-tiered indicator that visualises the rower's Check Factor
// (CF%) — the ratio of MIN speed to MAX speed within a single stroke,
// expressed as a percentage. A high CF% (green) means the boat barely slows
// between drives; a low CF% (red) means the rower "checks" the run hard on
// each recovery.
//
// Behaviour
//   • Reads the same 20 Hz-ish `strokeData` stream that the CheckFactorGraph
//     consumes (`[{ timestamp, speed, strokePhase }]`).
//   • Detects stroke boundaries by watching `strokePhase` wrap from ~1 → ~0
//     (same rule the WorkoutMonitorV2 demo path uses). At each new stroke
//     boundary it computes CF% from that stroke's min/max speed and freezes
//     the value until the next stroke closes — this matches how every other
//     stroke-based metric on the Monitor updates.
//   • Falls back to a rolling ~3 s window if no phase wrap has been seen yet
//     so the bar shows a plausible value the moment the boat is moving.
//
// Layout
//   • A tall bar, five colour zones stacked bottom→top.
//   • A solid black horizontal marker line at the current CF% value. The
//     line extends ~8 px past both edges of the bar (per product spec —
//     "about 2 mm on a real device").
//   • Below the bar: the CF% number ("94%") and tier label ("EXCELLENT")
//     rendered in the tier's colour.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';

// Tier definitions — bottom→top. `min` is inclusive lower bound.
export const CF_TIERS = [
  { min: 0,  max: 50, color: '#EF4444', label: 'POOR' },        // red
  { min: 50, max: 65, color: '#F97316', label: 'NEEDS WORK' },  // orange
  { min: 65, max: 75, color: '#EAB308', label: 'FAIR' },        // yellow
  { min: 75, max: 85, color: '#84CC16', label: 'GOOD' },        // lime green
  { min: 85, max: 100, color: '#22C55E', label: 'EXCELLENT' },  // green
];

function tierForCF(cf) {
  const clamped = Math.max(0, Math.min(100, cf));
  for (let i = CF_TIERS.length - 1; i >= 0; i--) {
    if (clamped >= CF_TIERS[i].min) return CF_TIERS[i];
  }
  return CF_TIERS[0];
}

export default function CheckFactorBar({ strokeData = [], className = '' }) {
  const [cf, setCf] = useState(0);
  const lastPhaseRef = useRef(0);
  const currentStrokeSpeedsRef = useRef([]);

  // Per-stroke CF% computation. Walk the incoming samples, group by stroke
  // boundary (phase wrap from high → low), and recompute at every boundary.
  useEffect(() => {
    if (!strokeData || strokeData.length === 0) return;

    // Only look at the tail — we recompute lazily from state kept in refs.
    // On first render, seed from the full window.
    const bucket = currentStrokeSpeedsRef.current;
    const samples = strokeData.slice(-40); // ~2 s worth at 20 Hz
    let nextCf = cf;

    for (const s of samples) {
      const speed = Number(s?.speed) || 0;
      const phase = Number(s?.strokePhase) || 0;
      const lastPhase = lastPhaseRef.current;

      // Stroke boundary detected — phase wrapped from near-1 back to near-0.
      if (phase < 0.1 && lastPhase > 0.9 && bucket.length > 4) {
        const speedsPositive = bucket.filter(v => v > 0.1);
        if (speedsPositive.length > 3) {
          const maxSpeed = Math.max(...speedsPositive);
          const minSpeed = Math.min(...speedsPositive);
          if (maxSpeed > 0) {
            nextCf = Math.round((minSpeed / maxSpeed) * 100);
          }
        }
        currentStrokeSpeedsRef.current = [];
      }

      currentStrokeSpeedsRef.current.push(speed);
      // Prevent unbounded growth if the boundary never triggers (e.g.
      // stationary boat): cap at ~4 s of samples and slide.
      if (currentStrokeSpeedsRef.current.length > 80) {
        currentStrokeSpeedsRef.current = currentStrokeSpeedsRef.current.slice(-80);
      }
      lastPhaseRef.current = phase;
    }

    // Fallback: if no boundary has fired yet but we have enough samples,
    // publish a rolling estimate so the bar isn't stuck at 0.
    if (nextCf === cf && strokeData.length > 20 && cf === 0) {
      const window = strokeData.slice(-60).map(d => Number(d?.speed) || 0).filter(v => v > 0.1);
      if (window.length > 10) {
        const maxSpeed = Math.max(...window);
        const minSpeed = Math.min(...window);
        if (maxSpeed > 0) nextCf = Math.round((minSpeed / maxSpeed) * 100);
      }
    }

    if (nextCf !== cf) setCf(nextCf);
  }, [strokeData, cf]);

  const tier = tierForCF(cf);
  // Marker vertical position: bottom=0%, top=100%. Clamp inside the bar so
  // the marker never overshoots the pixel bounds.
  const markerPct = Math.max(0, Math.min(100, cf));

  return (
    <div
      className={`crewz-cf-bar-wrap ${className}`}
      data-testid="check-factor-bar"
      aria-label={`Check factor ${cf} percent, ${tier.label}`}
    >
      <div className="crewz-cf-bar-label" data-testid="check-factor-heading">
        CHECK FACTOR
      </div>

      {/* The vertical bar. Zones are absolutely stacked so we can place the
          black marker line as another absolute child spanning the full
          bar width (+ overhang). */}
      <div className="crewz-cf-bar" data-testid="check-factor-bar-track">
        {/* Zones — bottom to top. Height is proportional to the zone's
            numeric range so the marker position lines up with the numeric
            axis (0 → 100). */}
        {CF_TIERS.map((z) => (
          <div
            key={z.label}
            className="crewz-cf-zone"
            style={{
              flexGrow: z.max - z.min,
              backgroundColor: z.color,
            }}
          />
        ))}

        {/* Current-value marker. Bottom offset = CF%. Line extends past both
            edges of the bar by ~8 px per product spec. */}
        <div
          className="crewz-cf-marker"
          data-testid="check-factor-marker"
          style={{ bottom: `calc(${markerPct}% - 2px)` }}
          aria-hidden="true"
        />
      </div>

      {/* Numeric + tier label */}
      <div className="crewz-cf-readout" data-testid="check-factor-readout">
        <div
          className="crewz-cf-value"
          style={{ color: tier.color }}
          data-testid="check-factor-value"
        >
          {cf}%
        </div>
        <div
          className="crewz-cf-tier"
          style={{ color: tier.color }}
          data-testid="check-factor-tier"
        >
          {tier.label}
        </div>
      </div>

      <style>{`
        .crewz-cf-bar-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
          width: 100%;
          height: 100%;
          padding: 5px 3px;
          min-height: 0;
          min-width: 0;
        }
        .crewz-cf-bar-label {
          font-size: clamp(7px, 0.7vw, 8px);
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--c-dim, #9AA4AE);
          text-align: center;
          line-height: 1;
        }
        .crewz-cf-bar {
          position: relative;
          width: clamp(11px, 1.8vw, 16px);
          flex: 1 1 auto;
          min-height: 72px;
          max-height: 100%;
          display: flex;
          flex-direction: column-reverse; /* stack zones from the bottom */
          border-radius: 4px;
          overflow: visible; /* marker overhang */
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35),
                      inset 0 0 0 1px rgba(255, 255, 255, 0.08);
        }
        .crewz-cf-zone {
          width: 100%;
          flex-shrink: 0;
          flex-basis: 0;
          min-height: 0;
        }
        .crewz-cf-zone:first-child { border-top-left-radius: 4px; border-top-right-radius: 4px; }
        .crewz-cf-zone:last-child  { border-bottom-left-radius: 4px; border-bottom-right-radius: 4px; }
        .crewz-cf-marker {
          position: absolute;
          left: -5px;
          right: -5px;
          height: 3px;
          background: #000;
          border-radius: 1px;
          box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.75);
          transition: bottom 240ms ease-out;
          pointer-events: none;
        }
        .crewz-cf-readout {
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1.05;
          gap: 1px;
        }
        .crewz-cf-value {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-weight: 800;
          font-size: clamp(11px, 1.6vw, 18px);
          letter-spacing: -0.02em;
        }
        .crewz-cf-tier {
          font-size: clamp(6px, 0.7vw, 8px);
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        /* Portrait — sideLeft becomes a shallow row above the hero, so the
           bar stays vertical but shorter, with the label + readout inline
           to keep the panel dense. */
        .crewz-cockpit.portrait .crewz-cf-bar-wrap {
          flex-direction: row;
          gap: 10px;
          padding: 4px 8px;
          align-items: center;
        }
        .crewz-cockpit.portrait .crewz-cf-bar {
          min-height: 60px;
          height: 100%;
          width: clamp(14px, 3vw, 22px);
        }
        .crewz-cockpit.portrait .crewz-cf-bar-label {
          font-size: 9px;
          max-width: 3.5em;
          text-align: right;
        }
      `}</style>
    </div>
  );
}
